import { create } from 'zustand'

export type ThemeMode = 'mono' | 'glass' | 'neo' | 'wood'

// Each theme has color palettes (mono has only 1, others have 4)
export const THEME_PALETTES: Record<ThemeMode, { name: string; colors: string[] }[]> = {
  mono: [
    { name: 'Classic', colors: ['#fafafa', '#fafafa', '#fafafa', '#fafafa'] },
  ],
  glass: [
    { name: 'Blue', colors: ['#4a8ec9', '#6baed6', '#9ecae1', '#c6dbef'] },
    { name: 'Pink', colors: ['#d4729a', '#e891b2', '#f0b4cc', '#f8d7e6'] },
    { name: 'Yellow', colors: ['#f7f027', '#eab308', '#facc15', '#fef08a'] },
    { name: 'White', colors: ['#c8cdd4', '#dde1e6', '#eef0f3', '#f8f9fa'] },
  ],
  neo: [
    { name: 'Green', colors: ['#037d50', '#7F9F7B', '#98FFB4', '#d8d8d8'] },
    { name: 'Navy', colors: ['#1a237e', '#3949ab', '#7986cb', '#e8eaf6'] },
    { name: 'Purple', colors: ['#4a148c', '#7b1fa2', '#ba68c8', '#f3e5f5'] },
    { name: 'Pink', colors: ['#880e4f', '#c2185b', '#f06292', '#fce4ec'] },
  ],
  wood: [
    { name: 'Willis', colors: ['#4b2f25', '#6c4738', '#8c6049', '#2a1a15'] },
    { name: 'Powell', colors: ['#fbbbad', '#ee8695', '#4a7a96', '#292831'] },
    { name: 'Morrison', colors: ['#002b59', '#005f8c', '#00b9be', '#9ff4e5'] },
    { name: 'Lubezki', colors: ['#353022', '#4f4530', '#6c5a3b', '#17130f'] },
  ],
}

// Neo theme dither effect colors per palette: [colorBack, colorFront, colorHighlight]
export const NEO_DITHER_COLORS: [string, string, string][] = [
  ['#011910', '#98FFB4', '#d8ffb0'], // Green palette
  ['#0a0f1e', '#7986cb', '#c5cae9'], // Navy palette
  ['#1a0a20', '#ba68c8', '#e1bee7'], // Purple palette
  ['#1a0a14', '#f06292', '#f8bbd9'], // Pink palette
]

// Wood theme halftone CMYK colors per palette: { back, c, m, y, k }
export const WOOD_HALFTONE_COLORS: { back: string; c: string; m: string; y: string; k: string }[] = [
  { back: '#efe0d1', c: '#4b2f25', m: '#6c4738', y: '#8c6049', k: '#18110f' }, // Willis
  { back: '#f8d8d4', c: '#fbbbad', m: '#ee8695', y: '#4a7a96', k: '#292831' }, // Powell
  { back: '#dff2ec', c: '#002b59', m: '#005f8c', y: '#00b9be', k: '#082238' }, // Morrison
  { back: '#e7dfcf', c: '#353022', m: '#4f4530', y: '#6c5a3b', k: '#12100d' }, // Lubezki
]

interface ThemeState {
  theme: ThemeMode
  paletteSeed: number
  colorPalette: number // 0-3
  cardRadius: 'sharp' | 'rounded'
  setTheme: (theme: ThemeMode) => void
  setColorPalette: (palette: number) => void
  setCardRadius: (radius: 'sharp' | 'rounded') => void
}

const STORAGE_KEY = 'nok.theme'
const PALETTE_STORAGE_KEY = 'nok.colorPalette'
const RADIUS_STORAGE_KEY = 'nok.cardRadius'

function readInitialTheme(): ThemeMode {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === 'mono' || raw === 'glass' || raw === 'neo' || raw === 'wood') return raw
  return 'mono'
}

function readInitialPalette(): number {
  const raw = localStorage.getItem(PALETTE_STORAGE_KEY)
  const num = parseInt(raw || '0', 10)
  return Number.isFinite(num) && num >= 0 && num <= 3 ? num : 0
}

function readInitialRadius(): 'sharp' | 'rounded' {
  const raw = localStorage.getItem(RADIUS_STORAGE_KEY)
  if (raw === 'sharp' || raw === 'rounded') return raw
  return 'sharp'
}

export const useThemeStore = create<ThemeState>((set) => ({
  theme: readInitialTheme(),
  paletteSeed: 0,
  colorPalette: readInitialPalette(),
  cardRadius: readInitialRadius(),
  setTheme: (theme) => {
    localStorage.setItem(STORAGE_KEY, theme)
    set((state) => ({
      theme,
      paletteSeed: state.theme === theme ? state.paletteSeed + 1 : state.paletteSeed,
    }))
  },
  setColorPalette: (palette) => {
    localStorage.setItem(PALETTE_STORAGE_KEY, String(palette))
    set({ colorPalette: palette })
  },
  setCardRadius: (radius) => {
    localStorage.setItem(RADIUS_STORAGE_KEY, radius)
    set({ cardRadius: radius })
  },
}))
